import { Component, OnInit, inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TelemetryService } from '../../services/telemetry.service';
import { Router } from '@angular/router';
import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb.component';
import { DateRangePickerComponent } from '../dashboard/components/date-range-picker/date-range-picker.component';
import { AppSelectorComponent } from '../dashboard/components/app-selector/app-selector.component';
import { SpaceContextService, SpaceContext } from '../../services/space-context.service';
import { Subject, Subscription, combineLatest } from 'rxjs';
import { delay, debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-sessions',
  standalone: true,
  imports: [CommonModule, FormsModule, ThinkingOrbComponent, DateRangePickerComponent, AppSelectorComponent],
  template: `
    <div class="p-8 max-w-7xl mx-auto min-h-screen">
      <!-- Header -->
      <div class="flex justify-between items-end mb-8 border-b border-zaxis-border pb-4">
        <div>
          <h1 class="text-3xl font-bold text-white uppercase tracking-tight">Explorador de Sesiones</h1>
          <p class="text-zaxis-accent mt-1 text-[11px] font-mono tracking-widest uppercase">Historial completo de telemetría de todos tus dispositivos XR vinculados.</p>
        </div>
        <div class="flex gap-2 items-center">
          <input type="text" [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange($event)" 
                 placeholder="Buscar por ID, dispositivo o app..." 
                 class="bg-zaxis-dark border border-zaxis-border px-4 py-2 text-[11px] font-mono tracking-widest text-white focus:outline-none focus:border-zaxis-accent w-64 rounded-none">
          
          <app-app-selector 
            [apps]="availableApps" 
            [selectedApp]="selectedApp" 
            (appChange)="onAppChange($event)">
          </app-app-selector>
          
          <app-date-range-picker
            [selectedTimeRange]="selectedTimeRange"
            [customStartDate]="customStartDate"
            [customEndDate]="customEndDate"
            (rangeChange)="onRangeChange($event)">
          </app-date-range-picker>
        </div>
      </div>

      <!-- Main View with Skeleton -->
      <!-- Custom Brutalist Skeleton Loading State -->
      <div *ngIf="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div *ngFor="let mock of [1, 2, 3, 4, 5, 6]"
             class="skeleton-box p-5 relative flex flex-col justify-between h-[240px]">
          <div>
            <div class="flex justify-between items-start mb-4">
              <div class="skeleton-text w-1/2"></div>
              <div class="skeleton-text w-1/4"></div>
            </div>
            <div class="skeleton-text title w-3/4"></div>
            
            <div class="grid grid-cols-4 gap-2 mt-4 mb-2">
              <div class="skeleton-text h-[40px] w-full" style="height: 40px; margin: 0"></div>
              <div class="skeleton-text h-[40px] w-full" style="height: 40px; margin: 0"></div>
              <div class="skeleton-text h-[40px] w-full" style="height: 40px; margin: 0"></div>
              <div class="skeleton-text h-[40px] w-full" style="height: 40px; margin: 0"></div>
            </div>
          </div>
          <div class="flex justify-between items-center mt-4 pt-3 border-t border-[#333]">
            <div class="skeleton-text w-1/4" style="margin: 0"></div>
            <div class="skeleton-text w-1/4" style="margin: 0"></div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="!loading && sessions.length === 0" class="text-center py-20 bg-black rounded-none border border-zaxis-border">
        <div class="flex justify-center mb-4">
          <i class="ph ph-terminal text-6xl text-gray-600"></i>
        </div>
        <p class="text-gray-400 text-sm font-mono uppercase tracking-widest">Sin señal de telemetría</p>
        <p class="text-gray-500 text-xs font-mono uppercase tracking-widest mt-2">Esperando conexión de dispositivo XR...</p>
      </div>

      <!-- Sessions Grid -->
      <div *ngIf="sessions.length > 0">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div *ngFor="let s of sessions"
               class="bg-zaxis-dark border border-zaxis-border p-5 relative overflow-hidden group hover:border-zaxis-accent transition-colors cursor-pointer flex flex-col justify-between rounded-none"
               (click)="viewSession(s)">
            <div class="absolute bottom-0 left-0 h-1 bg-zaxis-accent w-0 group-hover:w-full transition-all duration-500"></div>
            
            <div>
              <div class="flex justify-between items-start mb-4">
                <span class="text-[10px] text-gray-500 uppercase tracking-widest font-mono">
                  {{ s.deviceModel || s.device_serial || s.device_id }}
                </span>
                <div class="flex gap-1.5">
                  <span *ngIf="s.xr_context?.runtime === 'WebXR'" class="px-2 py-1 bg-zaxis-accent/10 text-zaxis-accent border border-zaxis-accent/20 rounded-none text-[9px] font-mono uppercase tracking-widest flex items-center">
                    WebXR
                  </span>
                  <span *ngIf="s.xr_context?.runtime && s.xr_context?.runtime !== 'WebXR'" class="px-2 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-none text-[9px] font-mono uppercase tracking-widest flex items-center">
                    {{ s.xr_context.runtime }}
                  </span>
                  <span *ngIf="isSessionLive(s)" class="flex items-center gap-1 px-2 py-1 bg-zaxis-success/10 text-zaxis-success border border-zaxis-success/20 rounded-none text-[9px] font-mono uppercase tracking-widest">
                    LIVE
                  </span>
                  <span *ngIf="s.crashed || s.hasCrash || !!s.exception" class="px-2 py-1 bg-zaxis-danger/10 text-zaxis-danger border border-zaxis-danger/20 rounded-none text-[9px] font-mono uppercase tracking-widest">
                    CRASH
                  </span>
                  <span *ngIf="!(s.crashed || s.hasCrash || !!s.exception) && s.errorCount > 0" class="px-2 py-1 bg-zaxis-warning/10 text-zaxis-warning border border-zaxis-warning/20 rounded-none text-[9px] font-mono uppercase tracking-widest">{{ s.errorCount }} ERRORES</span>
                </div>
              </div>
              <h3 class="text-sm font-bold text-white uppercase tracking-wider mb-2 group-hover:text-zaxis-accent transition-colors truncate">
                {{ s.appName || 'Sesión XR' }} <span class="text-[10px] text-gray-500 font-mono ml-1">v{{ s.appVersion || '1.0' }}</span>
              </h3>
              
              <!-- Metrics Grid -->
              <div class="grid grid-cols-4 gap-2 text-[10px] font-mono mt-4 mb-2">
                <div class="bg-black border border-zaxis-border p-2 text-center flex flex-col justify-center">
                  <span class="block text-gray-600 uppercase tracking-widest text-[9px] mb-1">FPS</span>
                  <span class="text-white tracking-tight">{{ getAvgFps(s) | number:'1.0-0' }}</span>
                </div>
                <div class="bg-black border border-zaxis-border p-2 text-center flex flex-col justify-center">
                  <span class="block text-gray-600 uppercase tracking-widest text-[9px] mb-1">RAM</span>
                  <span class="text-white tracking-tight">{{ s.maxRamUsage || 0 }}M</span>
                </div>
                <div class="bg-black border border-zaxis-border p-2 text-center flex flex-col justify-center">
                  <span class="block text-gray-600 uppercase tracking-widest text-[9px] mb-1">CPU</span>
                  <span class="text-white tracking-tight">{{ getAvgCpu(s) | number:'1.0-1' }}</span>
                </div>
                <div class="bg-black border border-zaxis-border p-2 text-center flex flex-col justify-center">
                  <span class="block text-gray-600 uppercase tracking-widest text-[9px] mb-1">GPU</span>
                  <span class="text-white tracking-tight">{{ getAvgGpu(s) | number:'1.0-1' }}</span>
                </div>
              </div>
            </div>

            <div class="flex justify-between items-center mt-4 pt-3 border-t border-zaxis-border">
              <span class="text-[10px] font-mono text-gray-500">{{ s.backend_received_at | date:'short' }}</span>
              <span class="text-[10px] font-mono uppercase tracking-widest text-zaxis-accent group-hover:translate-x-1 transition-transform">Inspeccionar &rarr;</span>
            </div>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="flex items-center justify-between mt-6">
          <button 
            (click)="prevPage()" 
            [disabled]="currentPage === 1"
            class="px-4 py-2 bg-zaxis-dark hover:bg-white/5 border border-zaxis-border rounded-none text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white hover:text-zaxis-accent">
            &larr; Anterior
          </button>
          
          <div class="text-[10px] font-mono text-gray-400 uppercase tracking-widest">
            Página <span class="text-white">{{ currentPage }}</span> / <span class="text-white">{{ totalPages }}</span>
            <span class="text-gray-600 ml-2">({{ totalSessions }} TTL)</span>
          </div>

          <button 
            (click)="nextPage()" 
            [disabled]="currentPage === totalPages || totalPages === 0"
            class="px-4 py-2 bg-zaxis-dark hover:bg-white/5 border border-zaxis-border rounded-none text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white hover:text-zaxis-accent">
            Siguiente &rarr;
          </button>
        </div>
      </div>


    </div>
  `
})
export class SessionsComponent implements OnInit, OnDestroy {
  private telemetryService = inject(TelemetryService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  public spaceContext = inject(SpaceContextService);

  sessions: any[] = [];
  loading = true;

  // Pagination state
  currentPage = 1;
  limit = 12;
  totalPages = 1;
  totalSessions = 0;

  // Filter state
  searchQuery = '';
  selectedTimeRange = '7d';
  customStartDate = '';
  customEndDate = '';
  availableApps: string[] = [];
  selectedApp = 'all';
  
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private contextSubscription?: Subscription;
  
  private currentContextId?: string;

  ngOnInit() {
    // Restore filter state from service
    this.searchQuery = this.telemetryService.sessionsSearchQuery();
    this.selectedTimeRange = this.telemetryService.sessionsTimeRange();
    this.customStartDate = this.telemetryService.sessionsCustomStartDate();
    this.customEndDate = this.telemetryService.sessionsCustomEndDate();
    this.selectedApp = this.telemetryService.sessionsSelectedApp();

    this.telemetryService.getApps().subscribe(apps => this.availableApps = apps);

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadSessions();
    });
    
    this.contextSubscription = this.spaceContext.activeContext$.subscribe(context => {
      if (context) {
        this.currentContextId = context.id;
        this.currentPage = 1;
        this.loadSessions();
      }
    });
  }
  
  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.contextSubscription?.unsubscribe();
  }

  onSearchChange(value: string) {
    this.telemetryService.sessionsSearchQuery.set(value);
    this.searchSubject.next(value);
  }

  onAppChange(app: string) {
    this.selectedApp = app;
    this.telemetryService.sessionsSelectedApp.set(app);
    this.currentPage = 1;
    this.loadSessions();
  }

  onRangeChange(event: { range: string, start?: string, end?: string }) {
    this.selectedTimeRange = event.range;
    this.telemetryService.sessionsTimeRange.set(event.range);
    
    if (event.range === 'custom') {
      this.customStartDate = event.start || '';
      this.customEndDate = event.end || '';
    } else {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    
    this.telemetryService.sessionsCustomStartDate.set(this.customStartDate);
    this.telemetryService.sessionsCustomEndDate.set(this.customEndDate);

    this.currentPage = 1;
    this.loadSessions();
  }

  private calculateDates(): { startDate?: string, endDate?: string } {
    if (this.selectedTimeRange === 'custom' && this.customStartDate && this.customEndDate) {
      return { startDate: this.customStartDate, endDate: this.customEndDate };
    }

    const end = new Date();
    const start = new Date();
    switch (this.selectedTimeRange) {
      case '15m': start.setMinutes(start.getMinutes() - 15); break;
      case '30m': start.setMinutes(start.getMinutes() - 30); break;
      case '1h': start.setHours(start.getHours() - 1); break;
      case '24h': start.setHours(start.getHours() - 24); break;
      case '7d': start.setDate(start.getDate() - 7); break;
      default: return {};
    }
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }

  loadSessions() {
    this.loading = true;
    const { startDate, endDate } = this.calculateDates();
    const search = this.searchQuery.trim() || undefined;
    const appFilter = this.selectedApp === 'all' ? undefined : this.selectedApp;
    const stream = this.telemetryService.getSessions(this.currentPage, this.limit, appFilter, startDate, endDate, search, this.currentContextId);
    const delayedStream = isPlatformBrowser(this.platformId) ? stream.pipe(delay(2000)) : stream;
    delayedStream.subscribe({
      next: (response) => {
        // Handle response either as array (old way) or paginated object (new way)
        if (Array.isArray(response)) {
          this.sessions = response;
          this.totalSessions = response.length;
          this.totalPages = 1;
        } else {
          this.sessions = response.data || [];
          this.totalSessions = response.total || 0;
          this.totalPages = response.totalPages || 1;
          this.currentPage = response.page || 1;
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading sessions', err);
        this.loading = false;
      }
    });
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadSessions();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadSessions();
    }
  }

  isSessionLive(session: any): boolean {
    if (session.crashed || session.hasCrash || !!session.exception) return false;
    if (!session.backend_received_at) return false;
    const receivedTime = new Date(session.backend_received_at).getTime();
    return (Date.now() - receivedTime) < 35000;
  }

  viewSession(session: any) {
    this.router.navigate(['/session', session.id], { state: { session } });
  }

  getAvgFps(session: any): number {
    if (!session.performanceCount || session.performanceCount === 0) return session.avgFps || 0;
    return (session.sumFps || 0) / session.performanceCount;
  }

  getAvgCpu(session: any): number {
    if (!session.cpuCount || session.cpuCount === 0) return 0;
    return (session.sumCpu || 0) / session.cpuCount;
  }

  getAvgGpu(session: any): number {
    if (!session.gpuCount || session.gpuCount === 0) return 0;
    return (session.sumGpu || 0) / session.gpuCount;
  }
}
